import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, BarChart3, Settings, TrendingUp, Target, Play, CheckCircle, Users, Zap, DollarSign } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getAuthUrl } from "@/utils/redirectUtils";

const SolopreneurPage = () => {
  const handleStartFree = () => {
    window.location.href = getAuthUrl();
  };

  const painPoints = [
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Marketing Takes Too Much Time",
      description: "You're spending 20+ hours a week on marketing instead of serving clients or building your business."
    },
    {
      icon: <Settings className="h-8 w-8 text-orange-500" />,
      title: "Complex Tools & Setup", 
      description: "Marketing platforms are confusing and take forever to set up properly for your business needs."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-orange-500" />,
      title: "Expensive Marketing Agencies",
      description: "Hiring agencies costs thousands monthly, but you need professional marketing to compete and grow."
    }
  ];

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-blue-500" />,
      title: "One-Click Campaign Creation",
      description: "Create professional marketing campaigns in minutes, not hours. AI handles the heavy lifting while you focus on your business."
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-blue-500" />,
      title: "Simple Performance Tracking",
      description: "Clear, easy-to-understand reports that show what's working. No confusing metrics or overwhelming dashboards."
    },
    {
      icon: <Target className="h-8 w-8 text-blue-500" />,
      title: "Smart Budget Optimization",
      description: "AI automatically adjusts your ad spend to get the best results within your budget. No wasted money on poor-performing ads."
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Customer-Finding Automation",
      description: "Automatically find and target your ideal customers across multiple platforms. Grow your business while you sleep."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Tell Us About Your Business",
      description: "Share what you do, who you serve, and your goals. Takes just 2 minutes to get started with our simple setup process."
    },
    {
      number: "2", 
      title: "AI Creates Your Marketing",
      description: "Our AI builds professional campaigns tailored to your business, audience, and budget. No marketing experience required."
    },
    {
      number: "3",
      title: "Watch Your Business Grow",
      description: "Get more customers automatically while you focus on what you do best. Track results with simple, clear reporting."
    }
  ];

  const benefits = [
    "Save 15+ hours per week on marketing",
    "Professional campaigns without the complexity", 
    "Affordable alternative to expensive agencies",
    "Simple setup - no technical skills needed",
    "Automatic optimization for better results",
    "Clear reporting you can actually understand"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads for Solopreneurs | Bootstrap Your Business | Upfixe</title>
        <meta name="description" content="Compete with bigger businesses using AI-powered Google Ads. No team needed, no expensive agency. Professional campaigns in 5 minutes. Start free." />
        <meta name="keywords" content="Google Ads for solopreneurs, solo business marketing, bootstrapped business advertising, one person business" />
        <meta property="og:title" content="Google Ads for Solopreneurs | Bootstrap Your Business | Upfixe" />
        <meta property="og:description" content="Compete with bigger businesses using AI-powered Google Ads. No team needed, no expensive agency. Professional campaigns in 5 minutes." />
        <link rel="canonical" href="https://upfixe.com/solopreneur" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Your Marketing, <br />
              <span className="text-blue-600">Automated.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Stop struggling with complex marketing tools. Get professional campaigns that actually work, without the headaches or high costs of hiring an agency.
            </p>
            <div className="flex justify-center mb-6">
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 font-semibold"
                onClick={handleStartFree}
              >
                Use It for Free
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">No credit card required • No complex setup • Professional results</p>
          </div>

          {/* Video Demo Section */}
          <section className="py-24 px-4 bg-gradient-to-br from-background to-muted/50 mb-20">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  See How Simple Marketing Can Be
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Watch how solopreneurs create professional campaigns in minutes, not hours. 
                  No technical skills needed—just results.
                </p>
              </div>

              <div className="relative max-w-4xl mx-auto mb-12">
                <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                  <iframe
                    src="https://www.youtube.com/embed/2LXRIONSvoI"
                    title="Upfixe Solopreneur Demo Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={handleStartFree}
                  className="text-lg px-8 bg-blue-600 hover:bg-blue-700"
                >
                  Use It for Free
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8"
                  onClick={() => window.open('https://youtu.be/2LXRIONSvoI', '_blank')}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Full Demo
                </Button>
              </div>
            </div>
          </section>

          {/* Problem Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Running a Solo Business?</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                You shouldn't have to choose between growing your business and doing the work you love.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {painPoints.map((point, index) => (
                <Card key={index} className="text-center p-6 border-orange-200 bg-orange-50/50">
                  <div className="flex justify-center mb-4">{point.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-20 bg-blue-50 p-12 rounded-2xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Marketing That Actually Works for Solo Businesses</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Get professional marketing campaigns without the complexity, cost, or time investment. Built specifically for solopreneurs who want results, not headaches.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Simple Tools, Professional Results</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Everything you need to market your business effectively, without the learning curve or monthly agency fees.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Get Started in Minutes</h2>
              <p className="text-xl text-muted-foreground">
                From setup to your first customers in less than 30 minutes.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-6 items-center mb-12 ${index === steps.length - 1 ? 'mb-0' : ''}`}>
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-lg text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-4">Ready to Automate Your Marketing?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Join thousands of solopreneurs who've automated their marketing and grown their businesses without the complexity or cost of traditional solutions.
            </p>
            <div className="flex justify-center mb-6">
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                onClick={handleStartFree}
              >
                Use It for Free
              </Button>
            </div>
            <p className="text-sm opacity-80">Simple setup • Professional results • No credit card required</p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SolopreneurPage;